import { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  CASH_APP_ACCOUNT_IDENTIFIER,
  PAYPAL_ACCOUNT_IDENTIFIER,
  VENMO_ACCOUNT_IDENTIFIER,
} from 'features/payment/constants';
import usePrepaidMethodReload from 'features/payment/hooks/use-prepaid-method-reload';
import {
  initialErrorState,
  initialPaymentState,
  testCardPaymentState,
} from 'features/payment/initial-state';
import {
  CCFormField,
  CardType,
  CardTypes,
  IApplePayDetails,
  IFraudPreventionValues,
  IGooglePayDetails,
  IPayPalDetails,
  IVenmoDetails,
  PaymentState,
} from 'features/payment/types';
import {
  checkIfValidTip,
  extractPaymentTypeForLD,
  getCCFormErrors,
  getFraudPreventionValues,
  isApplePay,
  isCashApp,
  isGooglePay,
  isPayPal,
  isVenmo,
  mapDeliveryToCCFormAddress,
  mapPaymentMethodCardTypes,
  onCCFormChange,
  splitExpiry,
} from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { STORE_UNAVAILABLE } from 'features/store/constants';
import { useRestaurantStatusChecker } from 'features/store/hooks/use-restaurant-checker';
import { pick } from 'lodash-es';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { CartPaymentCardType, ServiceMode } from 'generated/graphql-gateway';
import { ICommitOrderInput, IPayment } from 'hooks/commit-order/types';
import { useCommitOrder } from 'hooks/commit-order/use-commit-order';
import { useDistance } from 'hooks/geolocation/use-distance';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { useHandleCommitError } from 'pages/cart/hooks/use-handle-commit-error';
import { useHandlePspRefusedError } from 'pages/cart/hooks/use-handle-psp-refused-error';
import { useIsVehicleDescriptionEnabled } from 'pages/cart/hooks/use-is-vehicle-description-enabled';
import { getNonce } from 'remote/api/first-data';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import { useGooglePay } from 'state/google-pay/hooks/use-google-pay';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';
import {
  OrderInjectionFlowVariations,
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { DeliveryStatus, OrderStatus } from 'state/order/types';
import { useServiceModeLive } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { getConfigValue, isAndroid, isWeb } from 'utils/environment';
import { toError } from 'utils/errors';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import logger from 'utils/logger';
import { GraphQLError } from 'utils/network';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { useTotalTipCalculations } from '../../your-cart/totals/use-total-tip-calculations';

export const useOrderPayment = () => {
  const {
    addPaymentMethod,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    clearPaymentDetails,
    getEncryptionDetails,
    encryptionResult,
    isFirstData,
    isOrbital,
    loading: loadingPayments,
    setLoading: setLoadingPayments,
    paymentDetails,
    paymentMethods,
    prepaidReloadPaymentMethodId,
    setCheckoutPaymentMethodId,
    storePaymentDetails,
    transformPaymentValues,
    reloadPaymentValues,
    setReloadPaymentValues,
    setLastCommitOrderErrorDate,
  } = usePaymentContext();
  const { user } = useAuthContext();
  const googleConfig = getConfigValue({ key: 'google', defaultValue: {} });
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const fdUrl = getConfigValue({ key: 'firstData', defaultValue: {} })?.baseUrl;

  const router = useRouter();

  const {
    formatCurrencyForLocale,
    confirmStoreOnCheckout,
    shouldConfirmStoreOnCheckout,
  } = useUIContext();
  const { emptyCart } = useCart();
  const { tryPricing, serverOrder, tipAmount, isOrderPriceLoading } = useCartContext();
  const serviceMode = useServiceModeLive();

  const {
    subTotalCents = 0,
    taxCents = 0,
    rewardDiscountAmount,
    additionalDiscountAmount,
  } = useTotalTipCalculations({ serverOrder });

  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const enableOnetimePaymentAfterVault = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
  const skipVaultStep = enableOnetimePaymentAfterVault && isFirstData;
  const isOneTimeCardPayment = skipVaultStep
    ? true
    : Boolean(
        paymentDetails.state && !paymentDetails.state.saveCard && paymentDetails.state.cardNumber
      );
  const handleErrorModalOpen = useCallback(() => setErrorModalOpen(true), []);
  const handleErrorModalConfirm = useCallback(() => setErrorModalOpen(false), []);
  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
    onOpen: handleErrorModalOpen,
    onConfirm: handleErrorModalConfirm,
  });
  const oneTimePaymentFlowEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );

  const { deliveryAddress, alertOrderDeliveryMinimum, pickupDate } = useOrderContext();
  const { email, store } = useStoreContext();
  const { setIsStoreOrderUnavailableDialogOpen } = useUIContext();
  const { isRestaurantAvailable } = useRestaurantStatusChecker();

  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const isDeliveryQuoted =
    isDelivery && serverOrder?.delivery?.status === DeliveryStatus.QUOTE_SUCCESSFUL;

  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const userDetailsName = user?.details.name;
  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({ billingCountry, userDetailsName })
  );
  const [errors, setErrors] = useState(initialErrorState);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [isAddGiftCardMethodSelected, setIsAddGiftCardMethodSelected] = useState(false);
  const [isCommittingOrder, setCommittingOrder] = useState(false);
  const [isHandlingOrderCommit, setIsHandlingOrderCommit] = useState(false);
  const [commitOrderAfterPricing, setCommitOrderAfterPricing] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);

  const isVehicleDescriptionEnabled = useIsVehicleDescriptionEnabled();

  const fdAccessTokenFirstRequest = useRef<string | null>(null);
  const paymentNonceFirstRequest = useRef<string | null>(null);
  const accountIdentifierFirstRequest = useRef<string | null>(null);

  const distanceToStore = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const {
    commitOrder,
    commitResult: { called: commitOrderCalled, error: commitError, loading: isCommitOrderLoading },
    forceCommitOrder,
    success: commitOrderSuccess,
    failure: commitOrderFailure,
  } = useCommitOrder({
    rbiOrderId: serverOrder && serverOrder.rbiOrderId,
    distanceToStore,
    checkoutData: {
      savings: additionalDiscountAmount,
      rewardsSaving: rewardDiscountAmount,
      tax: taxCents,

      total: subTotalCents + taxCents,
      subtotal: subTotalCents,
    },
  });

  // hold on to the original orderId
  const prevOrderId = useRef<string | null>(null);
  useEffect(() => {
    if (serverOrder && !prevOrderId.current) {
      prevOrderId.current = serverOrder.rbiOrderId;
    }
  }, [serverOrder]);

  const { handleCommitError } = useHandleCommitError({
    fdAccountId: checkoutPaymentMethodId,
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError,
  });

  const { handlePspRefusedError } = useHandlePspRefusedError({
    openErrorDialog,
  });

  const isOrderStatusFailureHandledRef = useRef(false);
  useEffect(() => {
    if (!isOrderStatusFailureHandledRef.current && commitOrderFailure) {
      isOrderStatusFailureHandledRef.current = true;
      // present the generic error modal.
      handleCommitError(new GraphQLError([]));
    }
  }, [isOrderStatusFailureHandledRef, commitOrderFailure, handleCommitError]);

  const isPlacingOrder =
    isCommittingOrder ||
    isCommitOrderLoading ||
    (commitOrderCalled && !commitOrderSuccess && !commitError);

  const isPlacingOrderOrLoadingPayments = isPlacingOrder || loadingPayments;

  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);

  const shouldCommitOrder = [
    OrderInjectionFlowVariations.INJECT_NOW,
    OrderInjectionFlowVariations.INJECT_AND_HOLD,
    OrderInjectionFlowVariations.TIMED_FIRE,
  ].includes(orderInjectFlow as OrderInjectionFlowVariations);

  const nonSelectedPaymentMethods = paymentMethods.filter(
    method =>
      method.accountIdentifier !== checkoutPaymentMethodId ||
      method.fdAccountId !== checkoutPaymentMethodId
  );
  const selectedPaymentMethod = paymentMethods.find(
    method =>
      method.accountIdentifier === checkoutPaymentMethodId ||
      method.fdAccountId === checkoutPaymentMethodId
  );

  const allPaymentMethods = selectedPaymentMethod
    ? [selectedPaymentMethod, ...nonSelectedPaymentMethods]
    : nonSelectedPaymentMethods;
  const showPaymentMethodSelection = !isPlacingOrderOrLoadingPayments;

  const {
    reloadAddPaymentMethod,
    reloadAmount,
    reloadGiftCardAndCheckout,
    showReloadPaymentMethods,
    needReloadAgain,
  } = usePrepaidMethodReload({
    commitOrder,
    handleCommitError,
    allPaymentMethods,
    user,
    serverOrder,
    selectedPaymentMethod,
    shouldCommitOrder: !!shouldCommitOrder,
    storeEmail: email,
    tipAmount,
  });

  const showPrepaidTransferBalance = !!selectedPaymentMethod?.prepaid;

  useEffect(() => {
    const paymentType = extractPaymentTypeForLD(checkoutPaymentMethod);
    updateLaunchDarklyUser({ paymentType }, 'checkout payment method change');
  }, [checkoutPaymentMethod, selectedPaymentMethod]);

  /**
   * Persist payment values in payment context state
   */
  const persistPaymentValuesToState = useCallback(
    (data: {
      encryptedCN?: string;
      encryptedEM?: string;
      encryptedEY?: string;
      encryptedSC?: string;
      accountIdentifier?: string;
      fdAccessToken?: string;
      fdNonce?: string;
      creditType: CartPaymentCardType | null;
      fraudPreventionValues: IFraudPreventionValues;
      googlePayDetails?: IGooglePayDetails;
      applePayDetails?: IApplePayDetails;
      paypalDetails?: IPayPalDetails;
      venmoDetails?: IVenmoDetails;
      saveCard?: boolean;
      state: PaymentState;
    }) => {
      storePaymentDetails(data);
    },
    [storePaymentDetails]
  );

  const handlePaymentMethodSelected = (newFdAccountId?: string) => {
    if (!newFdAccountId) {
      setCheckoutPaymentMethodId('');
      return;
    }

    setShowAddPaymentMethod(false);
    setIsAddGiftCardMethodSelected(false);
    setCheckoutPaymentMethodId(newFdAccountId);
  };

  // when checkout payment method have been updated from payment context
  // the flags to show add new credit card or add gift card
  // have to be updated.
  useEffect(() => {
    if (checkoutPaymentMethodId) {
      const isAddNewCardSelected =
        checkoutPaymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD;
      if (isAddNewCardSelected) {
        setCheckoutPaymentMethodId('');
      }
      setShowAddPaymentMethod(isAddNewCardSelected);
      setIsAddGiftCardMethodSelected(false);
    }
  }, [setCheckoutPaymentMethodId, checkoutPaymentMethodId]);

  // adds First Data test card to improve QA experience
  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery }));
    }
  });

  useEffect(() => {
    // For one time payments, we already have payment values that have been stored in context.
    // If it is one time & we have card info present we need to update the payment details to use them
    if (paymentDetails.state && isOneTimeCardPayment) {
      setPaymentValues(paymentDetails.state);
    }
  }, [isOneTimeCardPayment, paymentDetails.state]);

  useEffect(() => {
    const testBillingAddress = pick(
      testCardPaymentState({
        isDelivery: paymentValues.billingAddressSameAsDelivery,
        paymentFieldVariations,
      }),
      ['billingStreetAddress', 'billingApt', 'billingCity', 'billingState', 'billingZip']
    );

    if (paymentValues.billingAddressSameAsDelivery) {
      setPaymentValues(prevState => ({
        ...prevState,
        ...mapDeliveryToCCFormAddress(deliveryAddress),
      }));
    } else if (autoFill) {
      setPaymentValues(prevState => ({ ...prevState, ...testBillingAddress }));
    }
  }, [
    paymentValues.billingAddressSameAsDelivery,
    deliveryAddress,
    autoFill,
    paymentFieldVariations,
  ]);

  const { startActivity } = useIOSLiveActivities();

  const startLiveActivityTracker = useCallback(() => {
    if (!serverOrder) {
      return;
    }
    // Start IOS Live Activity Widget
    startActivity(serverOrder);
  }, [serverOrder, startActivity]);

  const navigateToOrderConfirmation = useCallback(() => {
    if (!serverOrder) {
      return;
    }

    emptyCart();
    startLiveActivityTracker();

    hapticNotification({ type: HapticsNotificationType.SUCCESS });

    const orderConfirmationUrl = `${routes.orderConfirmation}/${serverOrder.rbiOrderId}?showToast=true`;

    if (isAndroid) {
      // On Android Fabric, dismissTo+push causes concurrent fragment transactions
      // that crash with "child already has a parent" (transparentModal presentation).
      // Replace is a single atomic operation that avoids this.
      router.replace(orderConfirmationUrl);
    } else {
      router.dismissTo(routes.menu);
      router.push(orderConfirmationUrl);
    }
  }, [emptyCart, router, serverOrder, startLiveActivityTracker]);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    if (commitOrderSuccess) {
      // Clear payment details after order is commited if card is not saved
      if (!paymentDetails.saveCard) {
        clearPaymentDetails();
      }

      navigateToOrderConfirmation();

      return;
    }

    if (serverOrder.status === OrderStatus.PRICE_SUCCESSFUL && commitOrderAfterPricing) {
      // If the orderId hasn't changed, it has not finished pricing as we get a new orderId when its done
      if (prevOrderId.current === serverOrder?.rbiOrderId || (isDelivery && !isDeliveryQuoted)) {
        return;
      }
      void handleOrderCommit();
    }
  }, [
    commitOrderSuccess,
    JSON.stringify(serverOrder),
    prevOrderId,
    isDelivery,
    isDeliveryQuoted,
    commitOrderAfterPricing,
  ]);

  const onChange = useCallback(
    (name: CCFormField, value: string) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [paymentValues, errors, supportedCardTypes]
  );

  const focusElementWithError = () => {
    // TODO: Remove queryselector and replace with ref's
    // TODO: RN - Remove usage of document
    // const element: HTMLElement | null = document.querySelector(`[name=${name}]`);
    const element = null as any;
    if (element) {
      element.focus();
    }
  };

  const validateCreditCardForm = useCallback(
    (didAttemptSubmit = false) => {
      const { formErrors, hasErrors } = getCCFormErrors(
        paymentValues,
        errors,
        paymentFieldVariations,
        billingCountry
      );

      if (hasErrors) {
        const nameOfFirstError = [
          'nameOnCard',
          'cardNumber',
          'cvv',
          'expiry',
          'billingStreetAddress',
          'billingApt',
          'billingCity',
          'billingState',
          'billingZip',
          'billingCountry',
        ].find(name => formErrors[name as keyof typeof formErrors]);

        if (nameOfFirstError) {
          // @ts-expect-error TS(2554) FIXME: Expected 0 arguments, but got 1.
          focusElementWithError(nameOfFirstError);
        }

        setErrors(prevState => ({ ...prevState, ...formErrors, didAttemptSubmit }));
        return false;
      }
      return true;
    },
    [paymentValues, errors, paymentFieldVariations, billingCountry]
  );

  const placeOrder = useCallback(async () => {
    if (!serverOrder) {
      return Promise.reject('Required parameter is not defined.');
    }

    const isPayPalPayment = isPayPal(selectedPaymentMethod?.accountIdentifier);
    const isVenmoPayment = isVenmo(selectedPaymentMethod?.accountIdentifier);
    const isCashAppPayment = isCashApp(selectedPaymentMethod?.accountIdentifier);

    const shouldValidateCreditCard =
      !isPayPalPayment &&
      !isVenmoPayment &&
      !isCashAppPayment &&
      (!reloadAddPaymentMethod || (reloadAddPaymentMethod && showReloadPaymentMethods));
    if (shouldValidateCreditCard) {
      const ccFormIsValid = validateCreditCardForm();
      if (!ccFormIsValid) {
        // this may only ever trigger in dev when LD autoFillTestCard === false but lets collect
        // data for now and also give the user a clue rather than absolutely nothing
        logger.error({
          message: 'Failed to placeOrder - ccFormIsValid is false',
          invalidFields: Object.entries(errors || {})
            .filter(([, v]) => !!v) // value here will have an error if the field has an error, otherwise it is empty
            .map(([k]) => k),
        });
        alert(
          logUserErrorMessage({
            message: t('errorWithPayment'),
            severity: LogUserErrorSeverity.Irrecoverable,
          })
        );
        return;
      }
    }

    const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);
    if (!isRestaurantAvailableForOrdering) {
      return setIsStoreOrderUnavailableDialogOpen(true);
    }

    if (!showAddPaymentMethod && selectedPaymentMethod?.prepaid) {
      return reloadGiftCardAndCheckout();
    }

    const reshapedPaymentValues = transformPaymentValues({
      paymentValues,
      paymentFieldVariations,
    });
    const fraudPreventionValues = getFraudPreventionValues(paymentValues);

    let paymentNonce;
    let fdAccessToken;
    let accountIdentifier: string | undefined;

    if (isFirstData && !isPayPalPayment && !isVenmoPayment && !isCashAppPayment) {
      if (fdAccessTokenFirstRequest.current && paymentNonceFirstRequest.current) {
        fdAccessToken = fdAccessTokenFirstRequest.current;
        paymentNonce = paymentNonceFirstRequest.current;
      } else {
        try {
          setLoadingPayments(true);
          const response = await getEncryptionDetails();

          fdAccessToken = fdAccessTokenFirstRequest.current = response.fdAccessToken;
          const nonceResponse = await getNonce(
            reshapedPaymentValues,
            response.fdPublicKey,
            response.fdApiKey,
            fdAccessToken,
            response.fdCustomerId ?? null,
            fdUrl,
            onlySendPostalCode,
            response.algorithm ?? undefined
          );
          const nonce = await nonceResponse.json();
          // TODO: Share paymentNonce for when we save a users payment/card below? [addPaymentMethod]
          paymentNonce = paymentNonceFirstRequest.current = nonce.token.tokenId;
        } catch (error) {
          openErrorDialog({
            message: t('paymentAddingError'),
            modalAppearanceEventMessage: 'Error: Fetching Encryption Details Failure',
            error: toError(error),
          });

          setLoadingPayments(false);
          return;
        }
      }
      // Save CC values to state necessary for "I'm here" flows
      persistPaymentValuesToState({
        fdAccessToken,
        fdNonce: paymentNonce,
        creditType: paymentValues.cardType,
        fraudPreventionValues,
        saveCard: paymentValues.saveCard,
        state: paymentValues,
      });

      setLoadingPayments(false);
    }

    // First-Data only since Adyen adds the payment method as part of the commitOrder
    if (!skipVaultStep) {
      if (
        user &&
        paymentValues.saveCard &&
        isFirstData &&
        !isPayPalPayment &&
        !isVenmoPayment &&
        !isCashAppPayment
      ) {
        if (accountIdentifierFirstRequest.current) {
          accountIdentifier = accountIdentifierFirstRequest.current;
        } else {
          try {
            accountIdentifier = accountIdentifierFirstRequest.current = await addPaymentMethod(
              reshapedPaymentValues
            );
          } catch (error) {
            logger.error({ error, message: 'Error adding payment method during checkout' });
            return;
          }
        }
      }
    }

    if (shouldCommitOrder) {
      let commitInput: ICommitOrderInput;

      if (isOrbital) {
        const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

        // Vaulted Payment
        if (selectedPaymentMethod?.accountIdentifier) {
          commitInput = {
            creditType: paymentValues.cardType,
            // order,
            payment: {
              billingAddress: reshapedPaymentValues.billingAddress,
              ccMetadata: {
                ccBin: fraudPreventionValues.ccBin,
                ccExpiryMonth: expiryMonth,
                ccExpiryYear: expiryYear,
                ccLast4: fraudPreventionValues.ccLast4,
              },
              orbitalInput: {
                accountIdentifier: selectedPaymentMethod?.accountIdentifier,
                savePaymentMethod: false,
              },
              fullName: fraudPreventionValues.fullName,
            },
            skipCoolingPeriod: false,
          };
          // One time payment
        } else {
          if (!encryptionResult || !('cryptCard' in encryptionResult)) {
            throw new Error('no orbital encryption results');
          }
          const {
            cryptCard,
            cryptCvv,
            integrityCheck: pieIntegrityCheck,
            pieFormat,
            subscriberId: pieSubscriberId,
            mode: pieMode,
            keyId: pieKeyID,
            phase: piePhaseId,
          } = encryptionResult;

          commitInput = {
            creditType: paymentValues.cardType,
            // order,
            payment: {
              billingAddress: fraudPreventionValues.billingAddress,
              ccMetadata: {
                ccBin: fraudPreventionValues.ccBin,
                ccExpiryMonth: expiryMonth,
                ccExpiryYear: expiryYear,
                ccLast4: fraudPreventionValues.ccLast4,
              },
              orbitalInput: {
                encryptedPayload: {
                  encryptedCardNum: cryptCard,
                  encryptedCvv: cryptCvv,
                  expiryMonth: reshapedPaymentValues.expiryDate?.month!,
                  expiryYear: reshapedPaymentValues.expiryDate?.year!,
                  pieFormat,
                  pieMode,
                  piePhaseId: `${piePhaseId}`,
                  pieIntegrityCheck,
                  pieSubscriberId,
                  pieKeyID,
                  cardBrand: paymentValues.cardType ? paymentValues.cardType.toLowerCase() : '',
                  bin: reshapedPaymentValues.cardNumber.slice(0, 6),
                },
                savePaymentMethod: oneTimePaymentFlowEnabled ? paymentValues.saveCard : false,
              },
              fullName: fraudPreventionValues.fullName,
            },
            skipCoolingPeriod: false,
          };
        }
      } else {
        // Firstdata
        if (isPayPalPayment) {
          if (!paymentDetails.paypalDetails) {
            throw new Error('PayPal payment details not available');
          }
          const { nonce, deviceData, details } = paymentDetails.paypalDetails;
          commitInput = {
            creditType: PAYPAL_ACCOUNT_IDENTIFIER,
            payment: {
              fullName: fraudPreventionValues.fullName,
              paypalDetails: {
                details,
                deviceData,
                nonce,
              },
            },
            skipCoolingPeriod: false,
          };
        } else if (isVenmoPayment) {
          if (!paymentDetails.venmoDetails) {
            throw new Error('Venmo payment details not available');
          }
          const { nonce, deviceData, details } = paymentDetails.venmoDetails;
          commitInput = {
            creditType: VENMO_ACCOUNT_IDENTIFIER,
            payment: {
              fullName: fraudPreventionValues.fullName,
              venmoDetails: {
                details,
                deviceData,
                nonce,
              },
            },
            skipCoolingPeriod: false,
          };
        } else if (isCashAppPayment) {
          if (!paymentDetails.cashappDetails) {
            throw new Error('Cash App payment details not available');
          }
          commitInput = {
            creditType: CartPaymentCardType.CASHAPP,
            payment: {
              fullName: '',
              cashAppInput: {
                fdAccessToken: paymentDetails.cashappDetails?.fdAccessToken,
                fdNonce: paymentDetails.cashappDetails?.fdNonce,
              },
              ccMetadata: {
                ccBin: paymentDetails.cashappDetails.virtualCard.cardNumber.slice(0, 6),
                ccExpiryMonth: paymentDetails.cashappDetails.virtualCard.expiryMonth,
                ccExpiryYear: paymentDetails.cashappDetails.virtualCard.expiryYear,
                ccLast4: paymentDetails.cashappDetails.virtualCard.cardNumber.slice(-4),
              },
            },
            skipCoolingPeriod: false,
          };
        } else {
          const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

          commitInput = {
            creditType: paymentValues.cardType,
            payment: {
              billingAddress: reshapedPaymentValues.billingAddress,
              ccMetadata: {
                ccBin: fraudPreventionValues.ccBin,
                ccExpiryMonth: expiryMonth,
                ccExpiryYear: expiryYear,
                ccLast4: fraudPreventionValues.ccLast4,
              },
              firstDataInput: skipVaultStep
                ? {
                    accountIdentifier: undefined,
                    fdAccessToken,
                    fdNonce: paymentNonce,
                    storePaymentMethod: paymentValues.saveCard,
                  }
                : {
                    accountIdentifier: paymentValues.saveCard ? accountIdentifier : undefined,
                    fdAccessToken: paymentValues.saveCard ? undefined : fdAccessToken,
                    fdNonce: paymentValues.saveCard ? undefined : paymentNonce,
                  },
              fullName: fraudPreventionValues.fullName,
            },
            skipCoolingPeriod: false,
          };
        }
      }

      return commitOrder({ ...commitInput, pickupTime: pickupDate?.toISOString() });
    }

    hapticNotification({ type: HapticsNotificationType.SUCCESS });
  }, [
    serverOrder,
    selectedPaymentMethod?.accountIdentifier,
    selectedPaymentMethod?.prepaid,
    reloadAddPaymentMethod,
    showReloadPaymentMethods,
    isRestaurantAvailable,
    store,
    showAddPaymentMethod,
    transformPaymentValues,
    paymentValues,
    paymentFieldVariations,
    isFirstData,
    skipVaultStep,
    shouldCommitOrder,
    validateCreditCardForm,
    errors,
    setIsStoreOrderUnavailableDialogOpen,
    reloadGiftCardAndCheckout,
    persistPaymentValuesToState,
    setLoadingPayments,
    getEncryptionDetails,
    fdUrl,
    onlySendPostalCode,
    openErrorDialog,
    user,
    addPaymentMethod,
    isOrbital,
    commitOrder,
    encryptionResult,
    oneTimePaymentFlowEnabled,
    paymentDetails.paypalDetails,
    paymentDetails.venmoDetails,
    paymentDetails.cashappDetails,
    pickupDate,
  ]);

  const { requestingPayment: isGooglePayLoading, requestGooglePayPayment } = useGooglePay({
    onError: error => {
      handlePspRefusedError(error, false);
      setLastCommitOrderErrorDate(Date.now());
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      handleCommitError(error);
      logger.error({ error, message: 'Failed to Place Order with GooglePay' });
    },
    onCompleted: async (data?: IGooglePayDetails) => {
      if (!data) {
        return logger.warn('User left the Google Pay Flow');
      }
      try {
        await processOrderWithAccount({ googlePayDetails: data });
      } catch (error) {
        handlePspRefusedError(error as ApolloError, false);
        setLastCommitOrderErrorDate(Date.now());
        logger.error({ error, message: 'Failed to Place Order with GooglePay (catch)' });
        handleCommitError(error as ApolloError);
      }
    },
  });

  const handleApplePay = async ({
    error,
    data,
  }: {
    error?: string;
    data?: IApplePayDetails;
  }): Promise<void> => {
    if (error) {
      handlePspRefusedError(error, false);
      setLastCommitOrderErrorDate(Date.now());
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      handleCommitError(error);
      logger.error({ error, message: 'Failed to Place Order with ApplePay' });
      return logger.warn(error);
    }
    if (data) {
      try {
        await processOrderWithAccount({ applePayDetails: data });
      } catch (error) {
        handlePspRefusedError(error as ApolloError, false);
        setLastCommitOrderErrorDate(Date.now());
        handleCommitError(error as ApolloError);
        logger.error({ error, message: 'Failed to Place Order with ApplePay (catch)' });
      }
    }
  };

  // FD apple pay reload
  const { requestApplePayPayment: prepaidRequestApplePayPayment } = useApplePay({
    handleApplePay,
    tipAmount,
    prepaid: true,
    serverOrder,
  });

  const processOrderWithAccount = useCallback(
    async (data?: { applePayDetails?: IApplePayDetails; googlePayDetails?: IGooglePayDetails }) => {
      if (selectedPaymentMethod?.prepaid) {
        const reloadPaymentDetails = data?.applePayDetails ?? data?.googlePayDetails;

        if (!reloadPaymentDetails && prepaidReloadPaymentMethodId && reloadAmount > 0) {
          const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
          if (prepaidReloadPaymentMethodId === CardTypes.GOOGLE_PAY) {
            // Get the checkout gateway configs for google pay
            const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
              transactionType: TransactionType.PREPAID,
              countryCode,
              googleConfig,
            });

            return requestGooglePayPayment({
              countryCode,
              currencyCode,
              total: reloadAmount,
              gatewayMerchantId,
              gateway,
            });
          } else if (prepaidReloadPaymentMethodId === CardTypes.APPLE_PAY) {
            const applePayParams = {
              countryCode,
              currencyCode,
              total: reloadAmount,
            };

            return prepaidRequestApplePayPayment(applePayParams);
          }
        }

        const result = await reloadGiftCardAndCheckout(reloadPaymentDetails);
        // Do NOT return for service modes that should not commit
        if (shouldCommitOrder) {
          return result;
        }
      }
      const paymentAccount = paymentMethods.find(method => {
        const accountId = method.accountIdentifier ?? method.fdAccountId ?? '';
        return accountId === checkoutPaymentMethodId;
      });
      if (!paymentAccount || !serverOrder) {
        return Promise.reject('Required parameters are not defined.');
      }

      const creditType = mapPaymentMethodCardTypes(paymentAccount.credit?.cardType);

      const fraudPreventionValues = getFraudPreventionValues(paymentValues);

      const accountIdentifier =
        paymentAccount.accountIdentifier ?? paymentAccount.fdAccountId ?? '';
      const commitInput = {
        creditType,
        rbiOrderId: serverOrder.rbiOrderId,
        storeEmail: email,
        serviceMode,
        pickupTime: pickupDate?.toISOString(),
      };
      const fullName = fraudPreventionValues.fullName;

      const applePayDetails = data?.applePayDetails;
      const googlePayDetails = data?.googlePayDetails;

      // The reason this is here is because we want to avoid committing the order EXCEPT for in these situations...
      // In other cases, we want to navigate to a separate route where the user must confirm being close by, before committing... hence the sort of complicated logic here.
      // TODO - this whole section deserves a proper refactor due to how important it is to conversions as a whole.

      if (shouldCommitOrder) {
        // Digital pay
        if (applePayDetails || googlePayDetails) {
          const digitalPayDetails = {
            applePayDetails,
            googlePayDetails,
            fullName,
          };

          return commitOrder({
            ...commitInput,
            payment: {
              ...digitalPayDetails,
            },
          });
        }

        if (checkoutPaymentMethodId === CASH_ACCOUNT_IDENTIFIER) {
          const paymentData: IPayment = {
            cashPayment: true,
            fullName,
          };
          return commitOrder({
            ...commitInput,
            creditType: CASH_ACCOUNT_IDENTIFIER,
            payment: paymentData,
          });
        }

        // Normal Pay
        if (isOrbital) {
          return commitOrder({
            ...commitInput,
            payment: {
              orbitalInput: {
                accountIdentifier,
              },
              fullName,
            },
          });
        }

        return commitOrder({
          ...commitInput,
          payment: {
            firstDataInput: {
              accountIdentifier,
            },
            fullName,
          },
        });
      }

      // This is a temporary hack. This will go away when we actually do a pseudo-injection here
      persistPaymentValuesToState({
        accountIdentifier,
        creditType: creditType!,
        fraudPreventionValues,
        googlePayDetails,
        applePayDetails,
        state: paymentValues,
      });
      // This is actually NEEDED to handle for dine-in and take-out orders
      // TODO - this above statement does not appear to be the case (and doesn't make sense) but too scared to remove.
      return navigateToOrderConfirmation();
    },
    [
      selectedPaymentMethod,
      paymentMethods,
      prepaidReloadPaymentMethodId,
      checkoutPaymentMethodId,
      isOrbital,
      serverOrder,
      paymentValues,
      email,
      serviceMode,
      shouldCommitOrder,
      persistPaymentValuesToState,
      reloadAmount,
      reloadGiftCardAndCheckout,
      billingCountry,
      googleConfig,
      requestGooglePayPayment,
      prepaidRequestApplePayPayment,
      commitOrder,
      navigateToOrderConfirmation,
      pickupDate,
    ]
  );

  const {
    requestingPayment: isApplePayLoading,
    formatApplePayCartPayload,
    requestApplePayPayment,
  } = useApplePay({
    tipAmount,
    serverOrder,
    handleApplePay,
  });

  const placeOrderWithAccount = useCallback(async () => {
    const accountIdentifier = paymentMethods.find(
      method =>
        method.accountIdentifier === checkoutPaymentMethodId ||
        method.fdAccountId === checkoutPaymentMethodId
    );
    if (!accountIdentifier || !serverOrder) {
      return Promise.reject('Required parameters are not defined.');
    }

    // This method is safe to use for all payment methods except for apple pay web
    // for apple pay web this check will be performed in the handler
    if (!(isApplePay(checkoutPaymentMethodId) && isWeb)) {
      const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);
      if (!isRestaurantAvailableForOrdering) {
        return setIsStoreOrderUnavailableDialogOpen(true);
      }
    }

    if (isApplePay(checkoutPaymentMethodId)) {
      try {
        const applePayPayload = formatApplePayCartPayload();
        if (!applePayPayload) {
          throw new Error('Could not format Apple Pay Payload');
        }
        return requestApplePayPayment(applePayPayload);
      } catch (e) {
        if (e instanceof Error && e.message === STORE_UNAVAILABLE) {
          return setIsStoreOrderUnavailableDialogOpen(true);
        }
      }
    }
    if (isGooglePay(checkoutPaymentMethodId)) {
      const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
      // Get the checkout gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PAYMENT,
        countryCode,
        googleConfig,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: serverOrder.cart.totalCents + (tipAmount ?? 0),
        gatewayMerchantId,
        gateway,
      });
    }

    return processOrderWithAccount();
  }, [
    billingCountry,
    formatApplePayCartPayload,
    googleConfig,
    isRestaurantAvailable,
    setIsStoreOrderUnavailableDialogOpen,
    tipAmount,
    checkoutPaymentMethodId,
    paymentMethods,
    processOrderWithAccount,
    requestApplePayPayment,
    requestGooglePayPayment,
    serverOrder,
    store,
  ]);

  const cardIsValid =
    !!selectedPaymentMethod ||
    showAddPaymentMethod ||
    isAddGiftCardMethodSelected ||
    paymentMethods.length === 0;

  const isValidTip = isDelivery
    ? checkIfValidTip({
        tipAmount,
        subTotalCents: serverOrder?.cart?.subTotalCents,
      })
    : true;

  const isValidReloadPaymentMethodWithCard =
    !reloadAddPaymentMethod || (reloadAddPaymentMethod && reloadPaymentValues.saveCard);

  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);

  const handleOrderCommit = useCallback(async () => {
    setCommittingOrder(true);
    isOrderStatusFailureHandledRef.current = false;
    try {
      if (!serverOrder) {
        throw new Error('Unable to get the rbiOrderId from serverOrder.');
      }

      const isPayPalRegistration =
        checkoutPaymentMethod?.accountIdentifier === PAYPAL_ACCOUNT_IDENTIFIER;

      const isVenmoRegistration =
        checkoutPaymentMethod?.accountIdentifier === VENMO_ACCOUNT_IDENTIFIER;

      const isCashAppRegistration =
        checkoutPaymentMethod?.accountIdentifier === CASH_APP_ACCOUNT_IDENTIFIER;

      const isPrepaidOrCash = selectedPaymentMethod?.cash || selectedPaymentMethod?.prepaid;
      const isNewPaymentMethod =
        showAddPaymentMethod ||
        reloadAddPaymentMethod ||
        isPayPalRegistration ||
        isVenmoRegistration ||
        isCashAppRegistration ||
        isOneTimeCardPayment;

      // Check if the order can be committed with an existing payment method
      if (isNewPaymentMethod && !isPrepaidOrCash) {
        await placeOrder();
      } else {
        await placeOrderWithAccount();
      }
    } catch (error) {
      const isCashAppPayment =
        checkoutPaymentMethod?.accountIdentifier === CASH_APP_ACCOUNT_IDENTIFIER;
      handlePspRefusedError(error as ApolloError, isCashAppPayment);

      setLastCommitOrderErrorDate(Date.now());

      // in some cases errors will not come from GQL, this ensures that we log them like apple pay issues!
      logger.error({
        message: 'Error committing order',
        error,
        accountIdentifier: checkoutPaymentMethod?.accountIdentifier,
      });

      if (!isCashAppPayment) {
        handleCommitError(error as ApolloError);
      }
    } finally {
    }

    return setCommittingOrder(false);
  }, [
    checkoutPaymentMethod,
    handleCommitError,
    isOneTimeCardPayment,
    placeOrder,
    placeOrderWithAccount,
    handlePspRefusedError,
    reloadAddPaymentMethod,
    selectedPaymentMethod,
    serverOrder,
    showAddPaymentMethod,
    setLastCommitOrderErrorDate,
  ]);

  useEffect(() => {
    if (isHandlingOrderCommit) {
      setIsHandlingOrderCommit(false);
      handleOrderCommit();
    }
  }, [handleOrderCommit, isHandlingOrderCommit]);

  const placeOrderAriaLabel = serviceMode
    ? t('placeOrderDescription', {
        serviceMode: getLocalizedServiceMode(serviceMode),
        price: formatCurrencyForLocale(serverOrder?.cart?.totalCents ?? 0),
      })
    : isPlacingOrder
    ? t('placingOrder')
    : undefined;

  const handleServiceModeUnavailableConfirm = useCallback(
    () => router.navigate(routes.storeLocator),
    [router]
  );
  const [ServiceModeUnavailableErrorDialog, openServiceModeUnavailableErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Service Mode Unavailable',
    onConfirm: handleServiceModeUnavailableConfirm,
  });

  const maybeCommitOrderAfterPricing = useCallback(() => {
    shouldConfirmStoreOnCheckout(true);

    if (isVehicleDescriptionEnabled && tryPricing) {
      setCommitOrderAfterPricing(true);
      tryPricing();
    } else {
      setIsHandlingOrderCommit(true);
    }
  }, [
    isVehicleDescriptionEnabled,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    shouldConfirmStoreOnCheckout,
    tryPricing,
  ]);

  const isProcessingPayment =
    loadingPayments ||
    isPlacingOrder ||
    isOrderPriceLoading ||
    isApplePayLoading ||
    isGooglePayLoading;

  return {
    cardIsValid,
    commitOrder: maybeCommitOrderAfterPricing,
    checkoutPaymentMethod,
    confirmStoreOnCheckout,
    enableDelivery,
    ErrorDialog,
    errors,
    focusElementWithError,
    setIsHandlingOrderCommit,
    handlePaymentMethodSelected,
    isLoadingPayments: loadingPayments,
    isApplePayLoading,
    isCashPayment: checkoutPaymentMethod?.accountIdentifier === CASH_ACCOUNT_IDENTIFIER,
    isErrorModalOpen,
    isGooglePayLoading,
    isOrderPriceLoading,
    isPlacingOrder,
    isCommitOrderLoading,
    isProcessingPayment,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    navigateToOrderConfirmation,
    needReloadAgain,
    onChange,
    openServiceModeUnavailableErrorDialog,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setCommitOrderAfterPricing,
    setReloadPaymentValues,
    setShowAddPaymentMethod,
    shouldCommitOrder,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    showReloadPaymentMethods,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
    commitOrderSuccess,
  };
};
